import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Formik, Form as FormMik } from 'formik';
import * as Yup from 'yup';

import { Container, Row, Form, Button, Modal } from 'react-bootstrap';

import api from '../services/apiClient';
import { useAuth } from '../hooks/auth';

import '../css/inputBox.css';
import '../css/suporte.css';

interface FormField {
  name: string;
  email: string;
  clinicName: string;
  product: string;
  question: string;
  department: string;
  terms: boolean;
}

interface DataDepartamento {
  seqDepartamento: number;
  nome: string;
}

const hasMeaningfulHtmlContent = (html: string): boolean => {
  return (
    html
      .replace(/<[^>]+>/g, '')
      .replace(/&nbsp;/g, ' ')
      .trim() !== ''
  );
};

const Support: React.FC = () => {
  const {
    signIn,
    setIdRequestSupport,
    setPosition,
    loadLogoEmpresaCliente,
    urlLogoEmpresaCliente,
    loadConfigAparrence,
    configAparrence,
  } = useAuth();
  const navigate = useNavigate();
  const [departments, setDepartments] = useState<DataDepartamento[]>([]);
  const [showAlert, setShowAlert] = useState(false);
  const [usuarioOnlineCount, setUsuarioOnlineCount] = useState(0);
  const [showAlertEnvioMsg, setShowAlertEnvioMsg] = useState(false);
  const [showAlertUserDepartament, setShowAlertUserDepartament] =
    useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termoCondicoesHtml, setTermoCondicoesHtml] = useState('');
  const [linkEmpresa, setLinkEmpresa] = useState('');
  const [searchParams] = useSearchParams();

  const initialValues: FormField = {
    name: '',
    email: '',
    clinicName: '',
    product: '',
    question: '',
    department: '',
    terms: false,
  };

  const hasTerms = hasMeaningfulHtmlContent(termoCondicoesHtml);

  const userOnline = async (link: string): Promise<void> => {
    await api.get(`/login/${link}`).then((response) => {
      const { success, data, totalCount } = response.data;
      if (success && !data && totalCount === 0) {
        setShowAlert(true);
      } else {
        setShowAlert(false);
      }
      setUsuarioOnlineCount(totalCount);
    });
  };

  useEffect(() => {
    const link = String(searchParams.get('emp')).toUpperCase();

    if (link === '' || link === 'NULL') {
      const linkEmpresaCache = localStorage.getItem('@LiveChat:linkEmpresa');
      const linkCache = `${window.location.href}?emp=${linkEmpresaCache}`;

      if (linkCache !== null) {
        window.location.href = linkCache;
        return;
      }
    }

    setLinkEmpresa(link);

    api.get(`api/departamento/cliente/${link}`).then((response) => {
      setDepartments(response.data.data);
    });

    api.get(`/parametro/TermoCondicoes/${link}`).then((response) => {
      const result = response.data;

      if (result.success) {
        setTermoCondicoesHtml(result.data.termoCondicoesHtml ?? '');
      }
    });

    userOnline(link);
    loadLogoEmpresaCliente(link);
    loadConfigAparrence(link);
  }, []);

  useEffect(() => {
    if (configAparrence?.corPrincipal) {
      document.documentElement.style.setProperty(
        '--cor-primaria',
        configAparrence.corPrincipal
      );
    }
  }, [configAparrence]);

  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .min(2, 'O nome deve ter mais do que 2 caracteres')
      .max(70, 'O nome deve ter menos do que 70 caracteres')
      .required('O nome é obrigatório'),
    email: Yup.string()
      .email('Insira um email válido')
      .max(70, 'O email deve ter menos do que 70 caracteres')
      .required('O email é obrigatório'),
    clinicName: Yup.string()
      .min(2, 'O nome da clínica deve ter mais do que 2 caracteres')
      .max(70, 'O nome da clínica deve ter menos do que 70 caracteres')
      .required('O nome da clínica é obrigatório'),
    product: Yup.string()
      .min(2, 'O nome do produto deve ter mais do que 2 caracteres')
      .max(70, 'O nome do produto deve ter menos do que 70 caracteres')
      .required('O nome do produto é obrigatório'),
    question: Yup.string()
      .max(1000, 'A pergunta deve ter menos do que 1000 caracteres')
      .required('A pergunta é obrigatória'),
    department: Yup.number()
      .min(1, 'O departamento é obrigatório')
      .required('O departamento é obrigatório'),
    terms: hasTerms
      ? Yup.boolean().oneOf([true], 'Você deve aceitar os termos de uso')
      : Yup.boolean(),
  });

  const submitChat = async (
    setSubmitting: (isSubmitting: boolean) => void,
    resetForm: () => void,
    values: FormField
  ): Promise<void> => {
    setSubmitting(true);
    await signIn({ user: values.name, nameClient: values.name });

    const token = localStorage.getItem('@LiveChat:token');
    const connectionId = localStorage.getItem('@LiveChat:connectionId');

    api.get(`/login/${linkEmpresa}`).then(async (response) => {
      const { success, data, totalCount } = response.data;

      if (success && !data && totalCount === 0) {
        setShowAlert(true);
      } else {
        const checkUserOnline = await api.get(
          `/DepartamentoUsuario/${values.department}/usuario`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (checkUserOnline.data.data < 1) {
          setShowAlertUserDepartament(true);
          setSubmitting(false);
          return;
        }
        setShowAlert(false);
        setUsuarioOnlineCount(totalCount);
      }

      const dataRequest = {
        name: values.name,
        email: values.email,
        clinicName: values.clinicName,
        product: values.product,
        question: values.question,
        department: values.department,
        connectionIdClient: connectionId,
        SupportOff: totalCount === 0,
      };

      api
        .post('/SolicitacaoAtendimento/start-chat', dataRequest, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((respChat) => {
          const dadosChat = respChat.data;
          if (response.data.success) {
            setIdRequestSupport(dadosChat.data.seqAtendimento);
            setPosition(dadosChat.data.posicaoAtendimento);
            localStorage.setItem(
              '@LiveChat:posicaoAtendimento',
              dadosChat.data.posicaoAtendimento
            );
            setSubmitting(false);
            resetForm();
            if (totalCount === 0) {
              setShowAlert(false);
              setShowAlertEnvioMsg(true);
            } else {
              navigate('/chat');
            }
          }
        });
    });
  };

  return (
    <>
      <Modal
        centered
        dialogClassName="modal-xl"
        show={showTermsModal}
        onHide={() => {
          setShowTermsModal(false);
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>Termos e Condições</Modal.Title>
        </Modal.Header>
        <Modal.Body
          style={{
            maxHeight: '78vh',
            minHeight: '55vh',
            overflowY: 'auto',
            textAlign: 'justify',
            wordBreak: 'break-word',
          }}
        >
          <div
            dangerouslySetInnerHTML={{
              __html: termoCondicoesHtml,
            }}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setShowTermsModal(false)}>
            Fechar
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showAlert}
        onHide={() => {
          setShowAlert(false);
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>Atenção</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p
            style={{
              fontSize: '1.25rem',
              textAlign: 'justify',
            }}
          >
            No momento não há nenhum atendente online!
          </p>
          <p
            style={{
              fontSize: '1rem',
              textAlign: 'justify',
            }}
          >
            Se desejar, deixe uma mensagem que nossa equipe de suporte irá
            entrar em contato o mais breve possível.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setShowAlert(false)}>
            Fechar
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showAlertEnvioMsg}
        onHide={() => {
          setShowAlertEnvioMsg(false);
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>Atenção</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p
            style={{
              fontSize: '1.25rem',
              textAlign: 'justify',
            }}
          >
            Messagem enviada com sucesso!
          </p>
          <p
            style={{
              fontSize: '1rem',
              textAlign: 'justify',
            }}
          >
            Obrigado por deixar uma menssagem, nossa equipe de suporte irá
            entrar em contato o mais breve possível.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setShowAlertEnvioMsg(false)}>
            Fechar
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showAlertUserDepartament}
        onHide={() => {
          setShowAlertUserDepartament(false);
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>Atenção</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p
            style={{
              fontSize: '1rem',
              textAlign: 'justify',
            }}
          >
            Não há nenhum atendente do departamento selecionado on-line nesse
            momento.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="primary"
            onClick={() => setShowAlertUserDepartament(false)}
          >
            Fechar
          </Button>
        </Modal.Footer>
      </Modal>

      <div className="div-main">
        <div className="sectionA">
          <img src="./assets/LiveChatLogo.svg" alt="Live-Chat Logo" />
        </div>
        <div className="sectionB fw-bold px-lg-2 pt-lg-2 px-xxl-3 pt-xxl-3">
          <div className="sectionB-logo">
            <img src={urlLogoEmpresaCliente} alt="Logo Cliente" />
          </div>
          <p className="title mt-lg-1">Fale Conosco</p>
          <p className="subtitle mb-lg-1">
            Antes de prosseguirmos, por favor, nos forneça algumas informações
            sobre você
          </p>
          <Container className="p-0 form-container">
            <Row className="">
              <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={async (values, { setSubmitting, resetForm }) => {
                  await submitChat(setSubmitting, resetForm, values);
                }}
              >
                {({
                  values,
                  errors,
                  touched,
                  handleChange,
                  handleBlur,
                  isSubmitting,
                  submitCount,
                }) => (
                  <FormMik>
                    <Form.Group
                      className="pt-lg-2  inputBoxPadding"
                      controlId="name"
                    >
                      <Form.Control
                        as="input"
                        type="text"
                        placeholder="Seu nome"
                        name="name"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.name}
                        className={`inputBox ${
                          touched.name && errors.name ? 'is-invalid' : ''
                        }`}
                      />
                    </Form.Group>

                    <Form.Group className="inputBoxPadding">
                      <Form.Control
                        type="email"
                        placeholder="Seu email"
                        name="email"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.email}
                        className={`inputBox
                          ${touched.email && errors.email ? 'is-invalid' : ''}`}
                      />
                    </Form.Group>

                    <Form.Group className="inputBoxPadding">
                      <Form.Control
                        type="text"
                        align-items-center
                        justify-content-center
                        placeholder="Empresa / Cidade / Estado"
                        name="clinicName"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.clinicName}
                        className={`inputBox
                            ${
                              touched.clinicName && errors.clinicName
                                ? 'is-invalid'
                                : ''
                            }`}
                      />
                    </Form.Group>

                    <Form.Group className="inputBoxPadding">
                      <Form.Control
                        type="text"
                        placeholder="Produto / No. Contrato"
                        name="product"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.product}
                        className={`inputBox
                           ${
                             touched.product && errors.product
                               ? 'is-invalid'
                               : ''
                           }`}
                      />
                    </Form.Group>
                    <Form.Group className="inputBoxPadding">
                      <Form.Select
                        areial-label="Departamento"
                        name="department"
                        placeholder="Departamento"
                        size="sm"
                        onChange={handleChange}
                        onClick={() => {
                          setShowAlertUserDepartament(false);
                        }}
                        onBlur={handleBlur}
                        value={values.department}
                        className={`${`inputBox ${
                          touched.department && errors.department
                            ? 'is-invalid'
                            : ''
                        }`}`}
                      >
                        <option value="0">Departamento</option>
                        {departments.map((department: DataDepartamento) => (
                          <option
                            key={department.seqDepartamento}
                            value={department.seqDepartamento}
                          >
                            {department.nome}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                    <Form.Group className="inputBoxPadding">
                      <Form.Control
                        id="inputQuestion"
                        type="text"
                        as="textarea"
                        placeholder="Dúvida"
                        name="question"
                        rows={1}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.question}
                        className={`inputBox ${
                          touched.question && errors.question
                            ? 'is-invalid'
                            : ''
                        }`}
                      />
                    </Form.Group>

                    {hasTerms && (
                      <Form.Group
                        className={
                          touched.terms && errors.terms ? '' : 'inputBoxPadding'
                        }
                      >
                        <div
                          className={`d-flex align-items-start text-checkbox ${
                            touched.terms && errors.terms ? 'is-invalid' : ''
                          }`}
                        >
                          <Form.Check
                            type="checkbox"
                            name="terms"
                            id="terms"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            checked={values.terms}
                            className="me-2"
                            label=""
                          />
                          <div style={{ paddingTop: '0.2rem' }}>
                            <span>Eu li e entendi os </span>
                            <button
                              type="button"
                              onClick={() => setShowTermsModal(true)}
                              style={{
                                color: '#4397D5',
                                background: 'transparent',
                                border: 0,
                                padding: 0,
                                fontWeight: 600,
                                textDecoration: 'underline',
                              }}
                            >
                              Termos e Condições
                            </button>
                            <span>.</span>
                          </div>
                        </div>

                        {touched.terms && errors.terms ? (
                          <div className="invalid-feedback d-block">
                            {errors.terms}
                          </div>
                        ) : null}
                      </Form.Group>
                    )}

                    {submitCount > 0 && Object.keys(errors).length > 0 ? (
                      <div className="invalid-icon-hint" role="note">
                        <span>Preencha os campos faltantes</span>
                        <span
                          className="invalid-icon-hint__icon"
                          aria-hidden="true"
                        />
                      </div>
                    ) : null}

                    <div className="bnt-div">
                      <Button
                        className="bnt-entrar"
                        variant={
                          usuarioOnlineCount === 0 ? 'warning' : 'primary'
                        }
                        type="submit"
                        disabled={isSubmitting}
                      >
                        {usuarioOnlineCount === 0
                          ? 'Deixar mensagem'
                          : 'Entrar'}
                      </Button>
                    </div>
                  </FormMik>
                )}
              </Formik>
            </Row>
          </Container>
        </div>
      </div>
    </>
  );
};

export default Support;
